import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { HexColorPicker } from 'react-colorful'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import Icon from 'components/priority/Icon/Icon'
import { selectedThemeAtom } from '@/atoms/printPackageAtoms'
import {
  clampEditorAuthoredPt,
  clampEditorLetterSpacingPt,
  displayedLetterSpacingPt,
  displayedTextCase,
  editorTextBoxSwatchRows,
  formatPt,
  removeColorTarget,
  toEditorTextBoxThemeColors,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCharacterStyleLayer,
  EditorResolvedStyle,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import ColorTileGrid from './ColorTileGrid'
import {
  FloatingPopover,
  POPOVER_GAP_PX,
  useFloatingPopoverPlacement,
} from './EditorTextBoxFloatingPopover'
import EditorTextBoxFontMenu from './EditorTextBoxFontMenu'
import { CASE_TILES, tileTitle } from './EditorTextBoxToolbar'
import * as T from './EditorTextBoxToolbar.styled'
import * as S from './EditorTextBoxModal.styled'

/** The step the − and + buttons take letter spacing by, in pt (the toolbar's). */
const LETTER_SPACING_STEP_PT = 0.1

type StyleBarMenu = 'case' | 'color' | 'font' | 'spacing'

type EditorTextBoxStyleBarProps = {
  effective: EditorResolvedStyle
  onCommit: (patch: EditorCharacterStyleLayer) => void
  fontFamilies: readonly string[]
  idp?: string
}

const EditorTextBoxStyleBar: FC<EditorTextBoxStyleBarProps> = ({
  effective,
  onCommit,
  fontFamilies,
  idp = 'editor-text-sb',
}) => {
  const { t } = useTranslation()
  const [openMenu, setOpenMenu] = useState<StyleBarMenu | null>(null)
  const [hex, setHex] = useState(effective.color)
  const [colorView, setColorView] = useState<'tiles' | 'hex'>('tiles')
  const [spacingDraft, setSpacingDraft] = useState<string | null>(null)
  const [sizeDraft, setSizeDraft] = useState<string | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const selectedTheme = useRecoilValue(selectedThemeAtom) as Record<
    string,
    unknown
  > | null
  const themeColors = useMemo(
    () => toEditorTextBoxThemeColors(selectedTheme),
    [selectedTheme],
  )

  const tid = (suffix: string) => ({ [`data-${idp}-${suffix}`]: true })

  // Under the trigger that opened it, at its left edge; the popover is in
  // the body, above the modal's backdrop, so the dialog's scroll never
  // clips it.
  const popoverStyle = useFloatingPopoverPlacement(openMenu, popoverRef, () => {
    const trigger = triggerRef.current?.getBoundingClientRect()
    if (!trigger) return null
    return {
      left: trigger.left,
      top: trigger.bottom + POPOVER_GAP_PX,
      flipTop: trigger.top,
    }
  })

  useEffect(() => {
    if (!openMenu) return undefined
    // A pick keeps the popover open for the next one; a press outside it or
    // Escape closes it, wherever focus sits (the hex input keeps focus after
    // Apply), and the modal shell leaves that Escape to the bar.
    const onDown = (e: MouseEvent) => {
      const t = e.target
      if (t instanceof Element && t.closest('[data-editor-text-sb-menu]'))
        return
      setOpenMenu(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      setOpenMenu(null)
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [openMenu])

  const toggleMenu =
    (menu: StyleBarMenu) => (e: ReactMouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      const opening = openMenu !== menu
      if (opening) triggerRef.current = e.currentTarget
      if (opening && menu === 'color') {
        setHex(effective.color)
        setColorView('tiles')
      }
      if (opening && menu === 'spacing') setSpacingDraft(null)
      setOpenMenu(opening ? menu : null)
    }

  const spacingShown = displayedLetterSpacingPt(effective)
  const stepSpacing = (delta: number) => {
    setSpacingDraft(null)
    onCommit({
      letterSpacing: clampEditorLetterSpacingPt(spacingShown + delta),
    })
  }
  const commitSpacingDraft = () => {
    const pt = Number(spacingDraft)
    setSpacingDraft(null)
    if (Number.isFinite(pt))
      onCommit({ letterSpacing: clampEditorLetterSpacingPt(pt) })
  }

  /** The props every popover of the bar shares: placed, marked as the bar's own chrome. */
  const popoverProps = (menu: StyleBarMenu) => ({
    ref: popoverRef,
    style: popoverStyle,
    $layer: 'modal' as const,
    'data-editor-text-sb-menu': '',
    'data-editor-text-popover': '',
    [`data-${idp}-popover`]: menu,
  })

  const boldOn = effective.fontWeight === 700
  const italicOn = effective.fontStyle === 'italic'
  const underlineOn = effective.textDecoration === 'underline'
  const strikeOn = effective.textDecoration === 'line-through'

  const shownSize = sizeDraft ?? formatPt(effective.fontSizePt)
  const stepSize = (delta: number) => {
    const cur = Number(sizeDraft ?? effective.fontSizePt)
    const base = Number.isFinite(cur) && cur > 0 ? cur : effective.fontSizePt
    setSizeDraft(null)
    onCommit({ fontSizePt: clampEditorAuthoredPt(base + delta) })
  }
  const commitSizeDraft = () => {
    const pt = Number(sizeDraft)
    setSizeDraft(null)
    if (Number.isFinite(pt) && pt > 0)
      onCommit({ fontSizePt: clampEditorAuthoredPt(pt) })
  }

  return (
    <>
      <S.MenuWrap>
        <T.Field
          type="button"
          {...tid('font')}
          title={t('editor.textBox.toolbar.fontFamily')}
          $active={openMenu === 'font'}
          onMouseDown={toggleMenu('font')}
        >
          <span
            style={{
              maxWidth: 110,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: `'${effective.fontFamily}'`,
            }}
          >
            {effective.fontFamily}
          </span>
          <Icon icon="chevronDown" width="11px" height="6px" />
        </T.Field>
        {openMenu === 'font' && (
          <FloatingPopover {...popoverProps('font')} $menu>
            <EditorTextBoxFontMenu
              families={fontFamilies}
              listProps={tid('font-list')}
              currentFamily={effective.fontFamily}
              onPick={(family) => onCommit({ fontFamily: family })}
            />
          </FloatingPopover>
        )}
      </S.MenuWrap>

      <T.FieldGroup {...tid('size-stepper')}>
        <T.Btn
          type="button"
          {...tid('size-dec')}
          title={t('editor.textBox.toolbar.decreaseFontSize')}
          onClick={() => stepSize(-1)}
        >
          <Icon icon="minusIcon" width="14px" height="14px" />
        </T.Btn>
        <T.SizeInput
          {...tid('size')}
          title={t('editor.textBox.toolbar.fontSize')}
          inputMode="decimal"
          value={shownSize}
          onFocus={(e) => {
            setSizeDraft(e.currentTarget.value)
            e.currentTarget.select()
          }}
          onChange={(e) =>
            setSizeDraft(e.currentTarget.value.replace(/[^0-9.]/g, ''))
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.stopPropagation()
              commitSizeDraft()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              setSizeDraft(null)
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault()
              stepSize(e.key === 'ArrowUp' ? 1 : -1)
            }
          }}
          onBlur={commitSizeDraft}
        />
        <T.Btn
          type="button"
          {...tid('size-inc')}
          title={t('editor.textBox.toolbar.increaseFontSize')}
          onClick={() => stepSize(1)}
        >
          <Icon icon="plus" width="11px" height="11px" />
        </T.Btn>
      </T.FieldGroup>

      <T.Btn
        type="button"
        {...tid('bold')}
        pressed={boldOn}
        aria-pressed={boldOn}
        title={t('editor.textBox.toolbar.bold')}
        onClick={() => onCommit({ fontWeight: boldOn ? 400 : 700 })}
      >
        <Icon icon="boldIcon" width="16px" height="16px" />
      </T.Btn>
      <T.Btn
        type="button"
        {...tid('italic')}
        pressed={italicOn}
        aria-pressed={italicOn}
        title={t('editor.textBox.toolbar.italic')}
        onClick={() => onCommit({ fontStyle: italicOn ? 'normal' : 'italic' })}
      >
        <Icon icon="italicIcon" width="16px" height="16px" />
      </T.Btn>
      <T.Btn
        type="button"
        {...tid('underline')}
        pressed={underlineOn}
        aria-pressed={underlineOn}
        title={t('editor.textBox.toolbar.underline')}
        onClick={() =>
          onCommit({ textDecoration: underlineOn ? 'none' : 'underline' })
        }
      >
        <Icon icon="underlineIcon" width="16px" height="16px" />
      </T.Btn>
      <T.Btn
        type="button"
        {...tid('strikethrough')}
        pressed={strikeOn}
        aria-pressed={strikeOn}
        title={t('editor.textBox.toolbar.strikethrough')}
        onClick={() =>
          onCommit({ textDecoration: strikeOn ? 'none' : 'line-through' })
        }
      >
        <Icon icon="strikethroughIcon" width="16px" height="16px" />
      </T.Btn>

      <S.MenuWrap>
        <T.Btn
          type="button"
          {...tid('case')}
          title={t('editor.textBox.toolbar.textCase')}
          onMouseDown={toggleMenu('case')}
        >
          <Icon icon="letterCaseIcon" width="16px" height="16px" />
        </T.Btn>
        {openMenu === 'case' && (
          <FloatingPopover {...popoverProps('case')} $pill>
            <T.Pill {...tid('case-list')}>
              {CASE_TILES.map((tile) => (
                <T.Btn
                  type="button"
                  key={tile.kind}
                  data-case-kind={tile.kind}
                  title={tileTitle(t, tile)}
                  pressed={displayedTextCase(effective) === tile.kind}
                  aria-pressed={displayedTextCase(effective) === tile.kind}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onCommit({ textCase: tile.kind })}
                >
                  <Icon icon={tile.icon} width="16px" height="16px" />
                </T.Btn>
              ))}
            </T.Pill>
          </FloatingPopover>
        )}
      </S.MenuWrap>

      <S.MenuWrap>
        <T.Btn
          type="button"
          {...tid('spacing')}
          title={t('editor.textBox.toolbar.letterSpacing')}
          onMouseDown={toggleMenu('spacing')}
        >
          <Icon icon="letterSpacingIcon" width="16px" height="16px" />
        </T.Btn>
        {openMenu === 'spacing' && (
          <FloatingPopover {...popoverProps('spacing')}>
            <T.FieldGroup {...tid('spacing-stepper')}>
              <T.Btn
                type="button"
                {...tid('spacing-dec')}
                title={t('editor.textBox.toolbar.decreaseLetterSpacing')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => stepSpacing(-LETTER_SPACING_STEP_PT)}
              >
                −
              </T.Btn>
              <T.SizeInput
                {...tid('spacing-input')}
                title={t('editor.textBox.toolbar.letterSpacingPt')}
                inputMode="decimal"
                value={spacingDraft ?? String(spacingShown)}
                onFocus={(e) => {
                  setSpacingDraft(e.currentTarget.value)
                  e.currentTarget.select()
                }}
                onChange={(e) =>
                  setSpacingDraft(
                    e.currentTarget.value.replace(/[^0-9.-]/g, ''),
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    commitSpacingDraft()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setSpacingDraft(null)
                  }
                }}
                onBlur={() => setSpacingDraft(null)}
              />
              <T.Btn
                type="button"
                {...tid('spacing-inc')}
                title={t('editor.textBox.toolbar.increaseLetterSpacing')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => stepSpacing(LETTER_SPACING_STEP_PT)}
              >
                +
              </T.Btn>
            </T.FieldGroup>
          </FloatingPopover>
        )}
      </S.MenuWrap>

      <T.Sep />

      <S.MenuWrap>
        <T.Btn
          type="button"
          {...tid('color')}
          title={t('editor.textBox.toolbar.textColor')}
          onMouseDown={toggleMenu('color')}
        >
          <T.Swatch hex={effective.color} />
        </T.Btn>
        {openMenu === 'color' && (
          <FloatingPopover {...popoverProps('color')} $menu>
            <ColorTileGrid
              hidden={colorView !== 'tiles'}
              attrPrefix={`${idp.replace(/^editor-text-/, '')}-color`}
              swatches={editorTextBoxSwatchRows(themeColors)}
              onPick={(picked) => {
                setHex(picked)
                onCommit({ color: picked })
              }}
              onRemove={() => {
                const restored = removeColorTarget(themeColors)
                setHex(restored)
                onCommit({ color: restored })
              }}
              onCustom={() => setColorView('hex')}
            />
            <T.HexView hidden={colorView !== 'hex'}>
              <T.TextBtn
                type="button"
                {...tid('color-tiles-back')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setColorView('tiles')}
              >
                {t('editor.textBox.toolbar.backToTiles')}
              </T.TextBtn>
              <HexColorPicker color={hex} onChange={setHex} />
              <T.Row>
                <T.TextInput
                  {...tid('color-input')}
                  value={hex}
                  onChange={(e) => setHex(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onCommit({ color: hex.trim() })
                  }}
                />
                <T.ApplyBtn
                  type="button"
                  {...tid('color-apply')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onCommit({ color: hex.trim() })}
                >
                  {t('editor.textBox.toolbar.apply')}
                </T.ApplyBtn>
              </T.Row>
            </T.HexView>
          </FloatingPopover>
        )}
      </S.MenuWrap>
    </>
  )
}

export default EditorTextBoxStyleBar
